/**
 * useTripData — shared per-trip data store. Wraps the existing
 * `/api/trips/{tripId}/full-context` endpoint which returns trip +
 * segments + payments + todos + visas + travelers + access in one
 * round-trip.
 *
 * Why a hook (the consolidated endpoint already exists):
 *
 *   Today `TripDetails` owns the fetch and prop-drills the result into
 *   `ItineraryView`, `TrelloBoard`, `VisaCompliance`, `TodoList`,
 *   `GadjetView`, etc. Anywhere a child needs to trigger a refresh,
 *   `onRefresh={fetchTripData}` has to be threaded through. With this
 *   hook, any descendant can call `tripDataStore.refetch(tripId)` (or
 *   subscribe via `useTripData(tripId)`) without prop drilling.
 *
 *   It also opens the door to:
 *     - `patchTripData(tripId, partial)` for in-place optimistic
 *       updates after a small mutation (e.g. one segment edit)
 *     - Cross-tab sync via storage events later
 *     - Multi-trip caching when the dashboard opens two trips at once
 *
 * Cache keys are tripIds. Each entry tracks its own loading/error and
 * in-flight guard. Cache resets on auth `user-cleared`.
 */
import { useEffect, useReducer, useRef } from "react";
import { api } from "../utils/api";

// --- module-level state -------------------------------------------------

const _cache = new Map();
// _cache.get(tripId) → { data, isLoading, error, inFlight }

const _subscribers = new Set();
const _notify = (tripId) => {
  for (const fn of _subscribers) fn(tripId);
};

// localStorage persistence — survives reloads + back-button navigations,
// so returning to a recently-viewed trip is instant on cold start.
// Keyed per trip + user (user_id is read off the user object lazily).
const LS_PREFIX = "trip-data-cache:";
const LS_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

const _userKey = () => {
  if (typeof window === "undefined") return null;
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.id || null;
  } catch {
    return null;
  }
};

const _readLS = (tripId) => {
  if (typeof window === "undefined" || !tripId) return null;
  const uid = _userKey();
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(`${LS_PREFIX}${uid}:${tripId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed?.timestamp) return null;
    if (Date.now() - parsed.timestamp > LS_MAX_AGE_MS) return null;
    return { data: parsed.data, timestamp: parsed.timestamp };
  } catch {
    return null;
  }
};

const _writeLS = (tripId, data) => {
  if (typeof window === "undefined" || !tripId || !data) return;
  const uid = _userKey();
  if (!uid) return;
  try {
    localStorage.setItem(
      `${LS_PREFIX}${uid}:${tripId}`,
      JSON.stringify({ timestamp: Date.now(), data }),
    );
  } catch {
    /* LS quota / serialization — non-fatal */
  }
};

const _initEntry = (tripId) => {
  let entry = _cache.get(tripId);
  if (!entry) {
    // Hydrate from LS so the very first read in a session is instant.
    const cached = _readLS(tripId);
    entry = {
      data: cached?.data || null,
      timestamp: cached?.timestamp || 0,
      isLoading: false,
      error: null,
      inFlight: null,
    };
    _cache.set(tripId, entry);
  }
  return entry;
};

// Cache freshness threshold — LS entries newer than this are considered
// "fresh enough" and skip the background revalidation. Older entries
// trigger a stale-while-revalidate refresh.
const FRESH_MS = 30 * 1000; // 30 seconds

export const tripDataStore = {
  getState(tripId) {
    const entry = _cache.get(tripId);
    return {
      data: entry?.data || null,
      isLoading: !!entry?.isLoading,
      error: entry?.error || null,
    };
  },

  subscribe(cb) {
    _subscribers.add(cb);
    return () => _subscribers.delete(cb);
  },

  async fetch(tripId) {
    if (!tripId) return null;
    const entry = _initEntry(tripId);
    if (entry.inFlight) return entry.inFlight;

    entry.isLoading = true;
    entry.error = null;
    _notify(tripId);

    entry.inFlight = (async () => {
      try {
        const res = await api.get(`/trips/${tripId}/full-context`);
        entry.data = res.data;
        entry.timestamp = Date.now();
        // Persist for instant first paint on next visit.
        _writeLS(tripId, res.data);
      } catch (e) {
        entry.error = e;
      } finally {
        entry.isLoading = false;
        entry.inFlight = null;
        _notify(tripId);
      }
    })();
    return entry.inFlight;
  },

  /**
   * Drop one trip's cache entry (and any in-flight request). Use when
   * a mutation invalidates the full context (e.g. trip rename).
   */
  invalidate(tripId) {
    _cache.delete(tripId);
    _notify(tripId);
  },

  /**
   * Merge a partial update into the cached trip data without a round
   * trip. Useful when the caller already has the updated rows in hand
   * (e.g. after PATCH /trips/{id}/visit-toggle). Falls back to a
   * full refetch if there's nothing cached yet.
   */
  patch(tripId, partial) {
    const entry = _cache.get(tripId);
    if (!entry || !entry.data) {
      return tripDataStore.fetch(tripId);
    }
    entry.data = { ...entry.data, ...partial };
    _notify(tripId);
    return Promise.resolve();
  },

  reset() {
    _cache.clear();
    // Wipe persisted trip caches too on logout.
    if (typeof window !== "undefined") {
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(LS_PREFIX)) keysToRemove.push(k);
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
      } catch {
        /* ignore */
      }
    }
    // Notify everyone — tripId === undefined signals a wholesale reset.
    _notify(undefined);
  },
};

if (typeof window !== "undefined") {
  window.addEventListener("auth:user-cleared", () => tripDataStore.reset());
}

// --- the hook -----------------------------------------------------------

export const useTripData = (tripId) => {
  const [, force] = useReducer((x) => x + 1, 0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const cb = (notifiedTripId) => {
      // Re-render only when an event for this trip (or a wholesale
      // reset) lands. Avoids needless renders when an unrelated trip
      // updates in the cache.
      if (notifiedTripId === tripId || notifiedTripId === undefined) {
        if (mountedRef.current) force();
      }
    };
    const unsubscribe = tripDataStore.subscribe(cb);
    if (tripId) {
      const entry = _cache.get(tripId) || _initEntry(tripId);
      // Stale-while-revalidate semantics:
      //   - no data + not loading → fetch (cold path)
      //   - have data but it's older than FRESH_MS → background refresh
      //     (user sees the cached data instantly, fresh data lands ~1s later)
      //   - have data and it's fresh → no network call
      if (!entry.data && !entry.isLoading) {
        tripDataStore.fetch(tripId);
      } else if (entry.data && Date.now() - (entry.timestamp || 0) > FRESH_MS && !entry.inFlight) {
        tripDataStore.fetch(tripId);
      }
    }
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [tripId]);

  const state = tripDataStore.getState(tripId);
  return {
    data: state.data,
    isLoading: state.isLoading,
    error: state.error,
    refetch: () => tripDataStore.fetch(tripId),
    invalidate: () => tripDataStore.invalidate(tripId),
    patch: (partial) => tripDataStore.patch(tripId, partial),
  };
};

export default useTripData;
